import { ColDef, KeyCreatorParams, ValueFormatterParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { NestedFieldPaths } from 'ag-grid-enterprise';
import { capitalize } from 'lodash-es';
import { useMemo } from 'react';

import * as yup from 'yup';
import { companyGridSchema } from '../../../../schemas/Company.schema';
import { templateAssignmentGridSchema } from '../../../../schemas/template-assignment-schema';
import { kpiActiveTemplatesListStateJ } from '../../../../services/state/KPI/KPITemplatesStateJ';
import { withOverrides } from '../../../../util/ag-grid-utils';
import { useSchemaToGrid } from '../../../../util/schema-utils';
import { IAssignedRowData } from './AssignedCompaniesData';
import { AssignedActionsRenderer } from './components/cellRenderers/AssignedActionsRenderer';
import { resolveTemplateDisplayName } from './components/cellRenderers/TemplateNamesCellRenderer';
import { CompanyColId } from './hooks/useKPICompaniesAllColDefs';

export function useAssignedCompaniesColDefs() {
  const schemaToColDefs = useSchemaToGrid();
  const activeTemplates = useAtomValue(kpiActiveTemplatesListStateJ);

  const activeTemplateUuids = useMemo(() => new Set(activeTemplates?.map((t) => t.uuid)), [activeTemplates]);

  return useMemo(() => {
    const assignedSchema = templateAssignmentGridSchema().concat(
      yup.object({
        company: companyGridSchema(),
      })
    );

    const overrideDefs: Partial<Record<NestedFieldPaths<IAssignedRowData>, ColDef<IAssignedRowData>>> = {
      ['company.id']: {
        colId: CompanyColId,
        rowGroup: true,
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
        hide: true,
        initialSort: 'asc',
        cellRendererParams: {
          displayLogoInGroupRow: true,
        },
        valueFormatter: (params: ValueFormatterParams<IAssignedRowData>) => {
          return params.data?.company?.name ?? params.node?.key ?? '';
        },
        keyCreator: (params: KeyCreatorParams<IAssignedRowData>) => {
          return params.data?.company?.name;
        },
      },
      ['templateUuid']: {
        valueFormatter: (params: ValueFormatterParams<IAssignedRowData>) => {
          const uuid: string = params.value ?? '';
          if (!uuid) {
            return '';
          }
          return resolveTemplateDisplayName(uuid, activeTemplateUuids);
        },
      },
      ['frequency']: {
        filterParams: {
          valueFormatter: (params: ValueFormatterParams<IAssignedRowData>) => {
            return params.value ? capitalize(params.value) : '';
          },
        },
      },
    };

    const columnDefs = schemaToColDefs(assignedSchema, [
      'company.id',
      'company.sectorId',
      'templateUuid',
      'frequency',
    ]) as ColDef[];

    return withOverrides(columnDefs, overrideDefs as Record<string, ColDef<IAssignedRowData>>).concat({
      headerName: '',
      maxWidth: 70,
      flex: 0,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      cellRenderer: AssignedActionsRenderer,
      pinned: 'right',
    });
  }, [schemaToColDefs, activeTemplateUuids]);
}

export const defaultColDef: ColDef<IAssignedRowData> = {
  flex: 1,
  resizable: true,
  sortable: true,
  filter: 'agSetColumnFilter',
  enableRowGroup: true,
  minWidth: 150,
};

export const defaultColGroupDef: ColDef<IAssignedRowData> = {
  filter: 'agGroupColumnFilter',
};
